import { useEffect, useRef } from 'react';
import { App } from '@capacitor/app';
import { clearAllSessions } from '../utils/sessionManager';
import logger from '../utils/logger';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? '';
const WARMUP_COOLDOWN_MS = 5 * 60 * 1000; // don't re-ping if already pinged within 5 min

// Module-level guard. The cold-start "was the app killed?" check MUST run exactly
// once per JS context (one real app launch) — never again on a React re-render.
//
// ROOT CAUSE this fixes (APK-only post-login bounce to /signin):
//   1. Cold start: effect runs, writes localStorage['app_was_running'] = 'true'.
//   2. User signs in -> login() -> setUser() -> AuthProvider re-renders -> the inline
//      `logout` gets a NEW function reference -> this effect's [onLogout] dep changes
//      -> effect tears down and RE-RUNS.
//   3. Re-run re-reads 'app_was_running' === 'true' (set 1 render ago), mistakes the
//      freshly-logged-in session for a "previous kill", calls clearAllSessions() +
//      onLogout(), wipes the token, and ProtectedRoute bounces to /signin.
// Web never hit this because the whole hook no-ops off-Capacitor.
let coldStartHandled = false;

function pingServer() {
  fetch(`${BACKEND_URL}/api/health`, { method: 'GET' })
    .then(() => logger.log('[Warmup] server warm'))
    .catch(() => {}); // fire-and-forget — never block or show errors
}

/**
 * Hook to monitor app lifecycle and enforce session security
 * 
 * Features:
 * - Detects when app is killed (via localStorage flag)
 * - Monitors background/foreground transitions
 * - Clears session if app was killed or if backgrounded for too long
 * 
 * @param {Object} options
 * @param {Function} options.onLogout - Callback to call when session is cleared
 * @param {number} options.timeoutMinutes - Minutes before background session expires (default: 30)
 */
export const useAppLifecycle = (onLogout, timeoutMinutes = 30) => {
    const lastPingRef = useRef(0);
    // Always invoke the freshest onLogout WITHOUT making it an effect dependency.
    // Keeping it out of the dep array is what stops the post-login re-render from
    // tearing down and re-running this effect (see coldStartHandled note above).
    const onLogoutRef = useRef(onLogout);
    onLogoutRef.current = onLogout;

    useEffect(() => {
        const isCapacitor = window.Capacitor?.isNativePlatform?.() ?? false;
        if (!isCapacitor) return;

        const APP_KILLED_FLAG = 'app_was_running';
        const LAST_ACTIVE_TIME = 'app_last_active';

        // ---- COLD START: runs exactly once per real app launch ----
        if (!coldStartHandled) {
            coldStartHandled = true;

            // Mitigation 1: warm up the serverless function on app start
            pingServer();
            lastPingRef.current = Date.now();

            // Was the app force-killed last time? (A clean exit clears the flag via the
            // background path below / beforeunload.) On a true kill, sessionStorage — and
            // with it the DEK — is already gone, so the user must re-auth anyway. Clearing
            // here only ever runs at startup BEFORE any fresh login, so it can never wipe a
            // just-created session.
            const wasRunning = localStorage.getItem(APP_KILLED_FLAG);
            if (wasRunning === 'true') {
                logger.log('[AppLifecycle] Cold start after unclean kill — clearing stale session');
                clearAllSessions();
                if (onLogoutRef.current) onLogoutRef.current();
            }

            // Set flag for current session
            localStorage.setItem(APP_KILLED_FLAG, 'true');
        }

        // 2. Listen for state changes
        const handleStateChange = App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) {
                logger.log('[AppLifecycle] App resumed');

                // Mitigation 2: re-warm the function if it may have gone cold while backgrounded
                const timeSinceLastPing = Date.now() - lastPingRef.current;
                if (timeSinceLastPing > WARMUP_COOLDOWN_MS) {
                    pingServer();
                    lastPingRef.current = Date.now();
                }

                // Check background timeout
                const lastActive = localStorage.getItem(LAST_ACTIVE_TIME);
                if (lastActive) {
                    const elapsedMinutes = (Date.now() - parseInt(lastActive)) / (1000 * 60);
                    if (elapsedMinutes > timeoutMinutes) {
                        logger.log(`[AppLifecycle] Session expired after ${Math.round(elapsedMinutes)} mins in background`);
                        clearAllSessions();
                        if (onLogoutRef.current) onLogoutRef.current();
                    }
                }

                // Reset flag
                localStorage.setItem(APP_KILLED_FLAG, 'true');
            } else {
                logger.log('[AppLifecycle] App went to background');
                localStorage.setItem(LAST_ACTIVE_TIME, Date.now().toString());
                // We keep APP_KILLED_FLAG as 'true' because if it's killed now,
                // it won't be cleared by the cleanup function.
            }
        });

        // 3. Cleanup on proper exit (if possible)
        const handleExit = () => {
            localStorage.removeItem(APP_KILLED_FLAG);
        };

        window.addEventListener('beforeunload', handleExit);

        return () => {
            handleStateChange.remove();
            window.removeEventListener('beforeunload', handleExit);
        };
        // onLogout intentionally excluded — it is read via onLogoutRef so an unstable
        // (re-created every render) callback can no longer churn this effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeoutMinutes]);
};
